import React, { useMemo } from 'react';
import { calculateFinancials } from '../../services/financialCalculations';

const BessSizingOptimizationTable = ({ inputs }) => {
    const { sensitivityData, energyCapacityRange, cyclesRange } = useMemo(() => {
        const baseEnergyCapacity = inputs.bessEnergyCapacity;
        const baseCycles = inputs.bessArbitrageCycles;

        const energyCapacityRange = [-0.4, -0.2, 0, 0.2, 0.4].map(mod => baseEnergyCapacity * (1 + mod));
        const cyclesRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => Math.max(0.8, Math.min(2.0, baseCycles * (1 + mod))));

        const sensitivityData = cyclesRange.map(cycles => {
            return energyCapacityRange.map(capacity => {
                const tempInputs = { ...inputs, bessEnergyCapacity: capacity, bessArbitrageCycles: cycles };
                const results = calculateFinancials(tempInputs);
                const bessInvestment = results.investmentPlan?.bessInvestment || 0;
                const bessRevenue = results.bessStatement?.reduce((a,b) => a + (b.arbitrageRevenue + b.ancillaryRevenue + b.capacityRevenue + b.demandChargeReduction), 0) || 0;
                return bessInvestment > 0 ? bessRevenue / bessInvestment : 0;
            });
        });

        return { sensitivityData, energyCapacityRange, cyclesRange };
    }, [inputs]);

    if (!inputs.bessEnabled) return null;

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-purple-800 dark:text-purple-100 mb-2">🔋 BESS Energy Capacity vs Arbitrage Cycles (BESS Revenue per $ Invested)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-purple-300 dark:border-purple-600">
                    <thead className="bg-purple-100 dark:bg-purple-700">
                        <tr>
                            <th className="p-2 border border-purple-300 dark:border-purple-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Cycles/Day ↓</span>
                                    <span className="font-normal text-xs">Energy Capacity →</span>
                                </div>
                            </th>
                            {energyCapacityRange.map((capacity, index) => (
                                <th key={index} className="p-2 border border-purple-300 dark:border-purple-600 font-medium">{capacity.toFixed(0)} MWh</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sensitivityData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                <td className="p-2 border border-purple-300 dark:border-purple-600 font-medium bg-purple-50 dark:bg-purple-700">
                                    {cyclesRange[rowIndex].toFixed(1)}
                                </td>
                                {row.map((ratio, colIndex) => (
                                    <td key={colIndex} className={`p-2 border border-purple-300 dark:border-purple-600 ${ratio > 0.3 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : ratio > 0.2 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}>
                                        {ratio.toFixed(3)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BessSizingOptimizationTable; 